import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class GoogleTranslateController extends Controller {
  static targets = ["customSelect", "scopedSelect", "globalSelectWrapper", "status", "statusText", "languageLoadStatus", "languageLoadMessage", "languageLoadReload"];
  static values = {
    pageLanguage: { type: String, default: "en" },
    cookieDomain: { type: String, default: "" },
    pollInterval: { type: Number, default: 1500 },
    pollTimeout: { type: Number, default: 15000 },
    stableMs: { type: Number, default: 350 }
  };

  connect() {
    this.shouldOpenSelect2AfterLoad = false;
    this.turboTranslationInProgress = false;
    this.initializeBrowserLanguageSession();
    this.handlePageShow = (event) => {
      const language = localStorage.getItem("selected_lang") || "en";
      if (!event.persisted || language === "en") return;
      this.prepareTurboTranslationSkeleton(document.body);
      this.setTranslationCookie(language);
      globalThis.location.reload();
    };
    this.handlePageHide = () => {
      if ((localStorage.getItem("selected_lang") || "en") !== "en") {
        this.prepareTurboTranslationSkeleton(document.body);
      }
    };
    globalThis.addEventListener("pageshow", this.handlePageShow);
    globalThis.addEventListener("pagehide", this.handlePageHide);

    this.handleBeforeVisit = this.beforeVisit.bind(this);
    this.handleBeforeCache = this.beforeCache.bind(this);
    this.handleBeforeRender = this.beforeRender.bind(this);
    this.handleBeforeFrameRender = this.beforeFrameRender.bind(this);
    this.handleFrameLoad = this.frameLoad.bind(this);
    this.handleBeforeStreamRender = this.beforeStreamRender.bind(this);
    this.handleNavigationStart = this.handleScopedNavigation.bind(this);
    this.handleModalShow = this.protectScopedModal.bind(this);
    this.handleGlobalModalShow = this.translateModal.bind(this);
    document.addEventListener("show.bs.modal", this.handleGlobalModalShow);
    document.addEventListener("turbo:before-visit", this.handleBeforeVisit);
    document.addEventListener("turbo:before-cache", this.handleBeforeCache);
    document.addEventListener("turbo:before-render", this.handleBeforeRender);
    document.addEventListener("turbo:before-frame-render", this.handleBeforeFrameRender);
    document.addEventListener("turbo:frame-load", this.handleFrameLoad);
    document.addEventListener("turbo:before-stream-render", this.handleBeforeStreamRender);

    this.bindCustomChange();
    this.bindScopedChange();
    this.setupGoogleTranslate();
    this.startPollingGoogleCombo();

    document.dispatchEvent(new CustomEvent("google-translate:ready", {
      detail: { controller: this }
    }));

    if (document.body.dataset.translationPending === "true") {
      requestAnimationFrame(() => this.translatePendingTurboPage());
    }
  }

  disconnect() {
    document.removeEventListener("show.bs.modal", this.handleGlobalModalShow);
    globalThis.removeEventListener("pageshow", this.handlePageShow);
    globalThis.removeEventListener("pagehide", this.handlePageHide);
    document.removeEventListener("turbo:before-visit", this.handleBeforeVisit);
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache);
    document.removeEventListener("turbo:before-render", this.handleBeforeRender);
    document.removeEventListener("turbo:before-frame-render", this.handleBeforeFrameRender);
    document.removeEventListener("turbo:frame-load", this.handleFrameLoad);
    document.removeEventListener("turbo:before-stream-render", this.handleBeforeStreamRender);
    this.cleanupScopedTranslation();
    this.resetScopedLanguage();
    this.stopScopedEventListeners();
    this.clearPollTimer();
    this.clearLanguageLoadStatus();
    clearTimeout(this.googleScriptRetryTimer);
    this.googleScriptRetryTimer = null;
    this.releaseScopedMask();
  }

  beforeVisit(event) {
    const language = localStorage.getItem("selected_lang") || "en";
    const destination = new URL(event.detail.url, globalThis.location.origin);
    const opensMessages = /^\/messages(?:\/|$)/.test(destination.pathname);
    if ((language === "en" && !opensMessages) || event.defaultPrevented) return;

    // Messages needs a fresh translation widget after a page change.
    event.preventDefault();
    // Avoid a persistent lock that survives Back navigation.
    this.setTranslationCookie(language);
    globalThis.location.assign(event.detail.url);
  }

  beforeCache() {
    if ((localStorage.getItem("selected_lang") || "en") !== "en") {
      Turbo.cache.exemptPageFromCache();
    }
    this.clearPollTimer();
    this.clearLanguageLoadStatus();
    this.cleanupScopedTranslation();
    this.resetScopedLanguage();
    this.releaseScopedMask();
    this.setSwitchingState(false);
    this.clearTurboTranslationSkeleton(document.body);
    this.revealTranslatedPage();
  }

  beforeRender(event) {
    const language = localStorage.getItem("selected_lang") || "en";
    if (language === "en") return;

    event.preventDefault();
    this.prepareTurboTranslationSkeleton(document.body);
    this.setTranslationCookie(language);
    globalThis.location.reload();
  }

  async translatePendingTurboPage() {
    const body = document.body;
    const language = localStorage.getItem("selected_lang") || "en";
    if (language === "en" || body.dataset.translationPending !== "true") return;

    if (globalThis.__itoonsTurboTranslationPromise) {
      await globalThis.__itoonsTurboTranslationPromise;
      if (body !== document.body || body.dataset.translationPending !== "true") return;
    }

    const gtCombo = document.querySelector(".goog-te-combo");
    const option = gtCombo?.querySelector(`option[value="${language}"]`);
    if (!gtCombo || !option) {
      this.clearTurboTranslationSkeleton(body);
      return;
    }

    const translation = this.changeTranslation(language)
      .finally(() => {
        this.clearTurboTranslationSkeleton(body);
        if (globalThis.__itoonsTurboTranslationPromise === translation) {
          globalThis.__itoonsTurboTranslationPromise = null;
        }
      });

    globalThis.__itoonsTurboTranslationPromise = translation;
    await translation;
  }

  beforeFrameRender(event) {
    const language = localStorage.getItem("selected_lang") || "en";
    if (language === "en") return;
    if (!event.target.closest(".modal") && event.target.id !== "chat-area" &&
        !event.target.closest("[data-dynamic-translation]") &&
        !event.detail.newFrame.querySelector("[data-dynamic-translation]")) return;

    event.target.dataset.translationPending = "true";
    this.prepareTurboTranslationSkeleton(event.detail.newFrame);
    if (event.target.closest(".modal")) {
      this.modalFrameTranslations ||= new WeakMap();
      // Observe before insertion so even a fast Google translation is captured.
      this.modalFrameTranslations.set(event.target,
        this.waitForTranslationDone({ root: event.target, requireMutation: true }));
    }
  }

  beforeStreamRender(event) {
    const stream = event.target;
    const targetId = stream.target;
    const target = targetId ? document.getElementById(targetId) : null;
    const scope = target?.closest("[data-local-translation-language]");
    const scopedLanguage = scope?.dataset.localTranslationLanguage;
    const language = scopedLanguage || localStorage.getItem("selected_lang") || "en";
    if (language === "en") return;
    if (!scope && !target?.closest(".modal, [data-dynamic-translation]") &&
        !stream.templateElement.content.querySelector(".modal, [data-dynamic-translation]")) return;

    const defaultRender = event.detail.render;
    event.detail.render = async streamElement => {
      const previousMessages = scopedLanguage && scope?.isConnected
        ? new Set(scope.querySelectorAll("[data-message-id]"))
        : new Set();
      // Hide incoming text before insertion, including append and prepend updates.
      this.markTranslationSkeleton(streamElement.templateElement.content);
      await defaultRender(streamElement);

      const renderedTarget = document.getElementById(targetId);
      const renderedScope = scope?.isConnected ? scope : renderedTarget;
      const addedMessages = scopedLanguage && renderedScope
        ? (renderedScope.matches("[data-message-id]") ? [renderedScope]
          : Array.from(renderedScope.querySelectorAll("[data-message-id]")))
          .filter(element => !previousMessages.has(element))
        : [];

      requestAnimationFrame(() => {
        const translationTarget = scopedLanguage ? renderedScope : renderedTarget;
        if (!translationTarget) return;
        if (scopedLanguage && !addedMessages.length) return;

        if (scopedLanguage) {
          addedMessages.forEach(element => this.prepareTurboTranslationSkeleton(element));
        } else {
          this.prepareTurboTranslationSkeleton(translationTarget);
        }
        this.dynamicTranslationQueue = (this.dynamicTranslationQueue || Promise.resolve())
          .catch(() => undefined)
          .then(() => scopedLanguage
            ? this.translateScopedDynamicContent(translationTarget, scopedLanguage, addedMessages)
            : this.translateDynamicContent(translationTarget, language));
      });
    };
  }

  async translateScopedDynamicContent(target, language, addedMessages) {
    if (!target.isConnected) return;
    const messages = target.matches("[data-message-id]")
      ? [target]
      : Array.from(target.querySelectorAll("[data-message-id]"));
    const pending = addedMessages.filter(element => element.isConnected &&
      element.dataset.localTranslationLanguage !== language);
    this.rememberOriginalMessages(pending);
    const protectedMessages = messages.filter(element => !pending.includes(element))
      .map(element => this.maskScopedElement(element));
    try {
      if (!pending.length) return;
      target.classList.remove("notranslate");
      target.removeAttribute("translate");
      await this.translateScopedWithWidget(target, language, { resetSameLanguage: false });
      for (const element of pending) {
        element.dataset.localTranslationLanguage = language;
        element.classList.add("notranslate");
        element.setAttribute("translate", "no");
      }
      target.dataset.localTranslationLanguage = language;
    } finally {
      this.restoreMaskedElements(protectedMessages);
      target.classList.add("notranslate");
      target.setAttribute("translate", "no");
      addedMessages.forEach(element => this.clearTurboTranslationSkeleton(element));
    }
  }

  async translateDynamicContent(target, language) {
    const gtCombo = document.querySelector(".goog-te-combo");
    const option = gtCombo?.querySelector(`option[value="${language}"]`);

    if (!gtCombo || !option) {
      this.clearTurboTranslationSkeleton(target);
      return;
    }

    try {
      await this.changeTranslation(language);
    } finally {
      this.clearTurboTranslationSkeleton(target);
    }
  }

  async frameLoad(event) {
    const frame = event.target;
    const language = localStorage.getItem("selected_lang") || "en";

    if (language === "en") return;
    if (!frame.closest(".modal") && frame.id !== "chat-area" && !frame.closest("[data-dynamic-translation]") &&
        !frame.querySelector("[data-dynamic-translation]")) return;
    const modalTranslation = this.modalFrameTranslations?.get(frame);
    if (modalTranslation) {
      this.modalFrameTranslations.delete(frame);
      try {
        await modalTranslation;
      } finally {
        this.clearTurboTranslationSkeleton(frame);
      }
      return;
    }
    if (!frame.dataset.translationPending || this.turboTranslationInProgress) return;

    const gtCombo = document.querySelector(".goog-te-combo");
    const option = gtCombo?.querySelector(`option[value="${language}"]`);
    if (!gtCombo || !option) {
      this.clearTurboTranslationSkeleton(frame);
      return;
    }

    this.turboTranslationInProgress = true;

    try {
      await this.changeTranslation(language);
    } finally {
      this.turboTranslationInProgress = false;
      this.clearTurboTranslationSkeleton(frame);
    }
  }

  async translateModal(event) {
    const modal = event.target;
    const language = localStorage.getItem("selected_lang") || "en";
    if (language === "en" || !modal?.matches(".modal") ||
        modal.closest(".notranslate, [translate='no']")) return;

    // A frame/stream render already owns the translation and its skeleton.
    if (modal.matches("[data-translation-pending]") ||
        modal.querySelector("[data-translation-pending]")) return;

    this.translatedModals ||= new WeakMap();
    const previous = this.translatedModals.get(modal);
    if (previous?.language === language && previous.text === modal.textContent) return;

    // Bootstrap emits show before displaying the dialog, so mask synchronously.
    this.prepareTurboTranslationSkeleton(modal);
    try {
      await this.nextFrame();
      const ready = await this.waitForTranslationDone({ root: modal });
      if (ready) this.translatedModals.set(modal, { language, text: modal.textContent });
    } finally {
      this.clearTurboTranslationSkeleton(modal);
    }
  }

  prepareTurboTranslationSkeleton(frame) {
    if (!frame) return;

    frame.dataset.translationPending = "true";
    this.markTranslationSkeleton(frame);
  }

  markTranslationSkeleton(root) {
    document.documentElement.classList.add("itoons-translation-pending");

    for (const element of this.translationSkeletonElements(root)) {
      element.classList.add("itoons-translation-skeleton");
      element.dataset.turboTranslationSkeleton = "true";
    }
  }

  clearTurboTranslationSkeleton(frame = null) {
    const roots = frame
      ? [frame]
      : Array.from(document.querySelectorAll("[data-translation-pending]"));

    for (const root of roots) {
      delete root.dataset.translationPending;
      if (root.dataset.turboTranslationSkeleton) {
        root.classList.remove("itoons-translation-skeleton");
        delete root.dataset.turboTranslationSkeleton;
      }
      for (const element of root.querySelectorAll("[data-turbo-translation-skeleton]")) {
        element.classList.remove("itoons-translation-skeleton");
        delete element.dataset.turboTranslationSkeleton;
      }
    }

    if (!document.querySelector("[data-translation-pending]")) {
      document.documentElement.classList.remove("itoons-translation-pending");
    }
  }

  translationSkeletonElements(root) {
    const elements = new Set();
    const ignoredSelector = [
      "script",
      "style",
      "noscript",
      "svg",
      "option",
      "textarea",
      "[contenteditable='true']",
      "[translate='no']",
      ".notranslate",
      ".visually-hidden",
      ".visually-hidden-focusable",
      ".spinner-border",
      ".spinner-grow"
    ].join(",");
    const broadTags = new Set([
      "BODY", "DIV", "SECTION", "MAIN", "HEADER", "FOOTER",
      "NAV", "ARTICLE", "ASIDE", "FORM", "UL", "OL", "TURBO-FRAME"
    ]);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const parent = node.parentElement;
      if (!node.textContent.trim() || !parent) continue;
      if (parent.closest(ignoredSelector) || broadTags.has(parent.tagName)) continue;
      elements.add(parent);
    }

    return elements;
  }

  openLanguageDropdown(event) {
    if (!this.scopedTranslation) this.releaseScopedMask();

    const select = this.activeLanguageSelect();
    const languagesLoaded = select?.options.length > 1;

    if (event && languagesLoaded) return;

    event?.preventDefault();
    this.shouldOpenSelect2AfterLoad = true;

    if (languagesLoaded) {
      this.openSelect2WhenReady(select);
      return;
    }

    this.loadLanguages(select, event);
  }

  loadLanguages(select, event) {
    event?.stopPropagation();
    if (this.hasLanguageLoadStatusTarget) {
      this.languageLoadStatusTarget.classList.remove("d-none");
    }
    this.setLanguageDropdownLoading(true);
    this.setLoadingOption(select);

    this.setupGoogleTranslate();
    this.startPollingGoogleCombo();
    this.openSelect2();
  }

  reloadPage() {
    globalThis.location.reload();
  }

  clearLanguageLoadStatus() {
    clearTimeout(this.languageLoadTimeout);
    this.languageLoadTimeout = null;
    if (this.hasLanguageLoadStatusTarget) {
      this.languageLoadStatusTarget.classList.add("d-none");
      this.languageLoadMessageTarget.textContent = "Loading languages. This may take a few moments…";
      this.languageLoadReloadTarget.classList.add("d-none");
    }
  }

  setLoadingOption(select) {
    if (!select) return;

    const loadingOption = select.options[0] || new Option();

    loadingOption.value = "__loading__";
    loadingOption.textContent = "Loading languages…";
    loadingOption.disabled = true;

    if (!select.options.length) {
      select.appendChild(loadingOption);
    }

    select.dataset.languagesLoading = "1";
    select.disabled = false;

    this.refreshSelect2(select);
  }

  openSelect2WhenReady(select) {
    if (!select.disabled) this.openSelect2();
  }

  openSelect2() {
    const select = this.activeLanguageSelect();
    if (!globalThis.jQuery || !select) return;

    this.showSelect2Container();

    const $select = jQuery(select);

    if (!$select.data("select2")) {
      setTimeout(() => this.openSelect2(), 150);
      return;
    }

    setTimeout(() => {
      $select.select2("open");
    }, 100);
  }

  showSelect2Container() {
    const select = this.activeLanguageSelect();
    const wrapper = select?.closest(".goog-te-gadget");
    if (!wrapper) return;

    wrapper.classList.remove("d-none");
  }

  bindCustomChange() {
    if (!this.hasCustomSelectTarget) return;
    if (this.customSelectTarget.dataset.changeBound === "1") return;

    this.customSelectTarget.dataset.changeBound = "1";

    this.customSelectTarget.addEventListener("change", async () => {
      if (globalThis.__gtSuppressLangChange) return;
      if (this.customSelectTarget.dataset.switching === "1") return;
      if (this.customSelectTarget.dataset.languagesLoading === "1") return;

      const lang = this.customSelectTarget.value;

      if (this.scopedTranslation) {
        this.cleanupScopedTranslation();
        this.releaseScopedMask();
      }

      if (document.querySelector("[data-local-translation-language]")) {
        await this.clearConversationTranslation();
      }

      this.enableGlobalMessageTranslation();
      this.setSwitchingState(true);
      localStorage.setItem("selected_lang", lang);
      Turbo.cache.clear();
      document.dispatchEvent(
        new CustomEvent("itoons:language-change", {
          detail: { language: lang }
        })
      );

      if (lang === "en") {
        await this.resetTranslation();
        return;
      }

      try {
        await this.changeTranslation(lang);
      } finally {
        this.setSwitchingState(false);
      }
    });
  }

  bindScopedChange() {
    if (!this.hasScopedSelectTarget) return;
    if (this.scopedSelectTarget.dataset.changeBound === "1") return;

    this.scopedSelectTarget.dataset.changeBound = "1";
    this.scopedSelectTarget.addEventListener("change", async () => {
      if (!this.scopedTranslation) return;
      if (this.scopedSelectTarget.dataset.switching === "1") return;
      if (this.scopedSelectTarget.dataset.languagesLoading === "1") return;

      await this.changeScopedTranslation(this.scopedSelectTarget.value);
    });
  }

  activeLanguageSelect() {
    if (this.scopedTranslation && this.hasScopedSelectTarget) {
      return this.scopedSelectTarget;
    }

    return this.hasCustomSelectTarget ? this.customSelectTarget : null;
  }

  async changeTranslation(language) {
    if (!this.scopedTranslation) this.enableGlobalMessageTranslation();
    this.setTranslationCookie(language);

    const gtCombo = document.querySelector(".goog-te-combo");
    const option = gtCombo?.querySelector(`option[value="${language}"]`);

    if (!gtCombo || !option) {
      globalThis.location.reload();
      return;
    }

    gtCombo.value = language;
    gtCombo.dispatchEvent(new Event("change", { bubbles: true }));
    await this.nextFrame();
    await this.waitForTranslationDone();
  }

  async openScopedLanguageDropdown(target) {
    if (!target) return;
    if (this.conversationResetPromise) await this.conversationResetPromise;
    if (!target.isConnected) return;

    this.cleanupScopedTranslation();
    this.releaseScopedMask();

    const messageElements = target.matches("[data-message-id]")
      ? [target]
      : Array.from(target.querySelectorAll("[data-message-id]"));

    const originalElements = [...messageElements, ...target.querySelectorAll(".message-time")];
    this.rememberOriginalMessages(originalElements);
    this.unlockScopedTarget(target);
    this.scopedMaskedElements = this.maskElementsOutside(target);
    this.startScopedEventListeners();

    this.scopedTranslation = {
      target,
      messageElements,
      originalElements,
      sourceLanguage:
        target.closest("[data-local-translation-language]")?.dataset.localTranslationLanguage ||
        null
    };

    this.setGlobalTranslatorVisible(false);
    this.openLanguageDropdown();
    this.bindScopedDropdownClose();
  }

  bindScopedDropdownClose() {
    if (!globalThis.jQuery || !this.hasScopedSelectTarget) return;

    const scope = this.scopedTranslation;
    const $select = jQuery(this.scopedSelectTarget);
    $select.off("select2:close.itoonsScopedTranslation");
    $select.one("select2:close.itoonsScopedTranslation", () => {
      setTimeout(() => {
        if (this.scopedTranslation !== scope) return;
        if (this.scopedSelectTarget.dataset.switching === "1") return;

        this.cleanupScopedTranslation();
        this.releaseScopedMask();
        this.restoreSelectedLanguage();
      }, 0);
    });
  }

  async changeScopedTranslation(language) {
    const scope = this.scopedTranslation;
    if (!scope) return;

    this.scopedSelectTarget.dataset.switching = "1";
    this.scopedSelectTarget.disabled = true;
    scope.target.setAttribute("aria-busy", "true");

    try {
      const restored = language === this.pageLanguageValue &&
        this.restoreOriginalMessages(scope.originalElements || scope.messageElements);
      if (!restored && (language !== scope.sourceLanguage || language === this.pageLanguageValue)) {
        await this.translateScopedWithWidget(scope.target, language);
      }

      scope.target.setAttribute("translate", "no");
      scope.target.classList.add("notranslate");

      for (const element of scope.messageElements) {
        element.setAttribute("translate", "no");
        element.classList.add("notranslate");
        element.dataset.localTranslationLanguage = language;
      }
      scope.target.dataset.localTranslationLanguage = language;
    } catch (error) {
      this.showScopedTranslationError(error);
    } finally {
      this.cleanupScopedTranslation();
      this.scopedSelectTarget.dataset.switching = "0";
      this.scopedSelectTarget.disabled = false;
      this.restoreSelectedLanguage(scope.target.dataset.localTranslationLanguage);
      this.refreshSelect2();
    }
  }

  async translateScopedWithWidget(target, language, { resetSameLanguage = true } = {}) {
    const gtCombo = document.querySelector(".goog-te-combo");
    let option = gtCombo?.querySelector(`option[value="${language}"]`);
    if (gtCombo && !option && language === this.pageLanguageValue) {
      option = this.createLanguageOption(language, "Original language");
      gtCombo.appendChild(option);
    }
    if (!gtCombo || !option) throw new Error("Google Translate is not ready");

    const maskedElements = this.scopedMaskedElements || this.maskElementsOutside(target);
    const pageLanguage = localStorage.getItem("selected_lang") || "en";
    let translated = false;

    try {
      if (resetSameLanguage && gtCombo.value === language) {
        gtCombo.value = "en";
        gtCombo.dispatchEvent(new Event("change", { bubbles: true }));
        await this.nextFrame();
        await this.waitForTranslationDone();
      }

      this.setTranslationCookie(language);
      const translationDone = this.waitForTranslationDone({ root: target, requireMutation: true });
      gtCombo.value = language;
      gtCombo.dispatchEvent(new Event("change", { bubbles: true }));
      await this.nextFrame();
      if (!await translationDone) {
        throw new Error("The conversation could not be translated. Please try again.");
      }

      target.setAttribute("translate", "no");
      target.classList.add("notranslate");

      translated = true;
    } finally {
      if (pageLanguage !== language) {
        pageLanguage === "en"
          ? this.clearTranslationCookies()
          : this.setTranslationCookie(pageLanguage);
      }
      if (translated) {
        this.scopedMaskedElements = maskedElements;
      } else {
        this.restoreMaskedElements(maskedElements);
        this.scopedMaskedElements = null;
      }
    }
  }

  rememberOriginalMessages(messages) {
    this.originalMessages ||= new WeakMap();
    // Only capture source content, before the widget has translated it.
    if ((localStorage.getItem("selected_lang") || "en") !== this.pageLanguageValue) return;

    for (const message of messages) {
      if (message.closest("[data-local-translation-language]") || this.originalMessages.has(message)) continue;
      this.originalMessages.set(message, this.captureMessageSnapshot(message));
    }
  }

  captureMessageSnapshot(node) {
    return {
      node,
      text: node.nodeType === 3 ? node.textContent : null,
      children: Array.from(node.childNodes, child => this.captureMessageSnapshot(child))
    };
  }

  restoreMessageSnapshot({ node, text, children }) {
    if (text !== null) {
      node.textContent = text;
      return;
    }
    children.forEach(child => this.restoreMessageSnapshot(child));
    // Reuse nodes so message actions and event handlers remain connected.
    node.replaceChildren(...children.map(child => child.node));
  }

  restoreOriginalMessages(messages) {
    if (!messages.every(message => this.originalMessages?.has(message))) return false;
    messages.forEach(message => this.restoreMessageSnapshot(this.originalMessages.get(message)));
    return true;
  }

  unlockScopedTarget(target) {
    const translatedElements = [
      target,
      ...target.querySelectorAll("[data-local-translation-language]")
    ];

    for (const element of translatedElements) {
      element.classList.remove("notranslate");
      element.removeAttribute("translate");
    }
  }

  maskElementsOutside(_target) {
    const elements = document.querySelectorAll(
      "nav.sidebar, .main-header, #user-list, .modal, [role='dialog'], .swal2-container"
    );

    return Array.from(elements).map((element) => this.maskScopedElement(element));
  }

  maskScopedElement(element) {
    const state = {
      element,
      hadNoTranslateClass: element.classList.contains("notranslate"),
      translateAttribute: element.getAttribute("translate")
    };

    element.classList.add("notranslate");
    element.setAttribute("translate", "no");
    return state;
  }

  protectScopedModal(event) {
    if (!this.scopedMaskedElements) return;

    const modal = event.target;
    if (!modal || this.scopedMaskedElements.some(({ element }) => element === modal)) return;

    this.scopedMaskedElements.push(this.maskScopedElement(modal));
  }

  restoreMaskedElements(states) {
    for (const { element, hadNoTranslateClass, translateAttribute } of states) {
      if (!element.isConnected) continue;
      if (!hadNoTranslateClass) element.classList.remove("notranslate");

      if (translateAttribute === null) {
        element.removeAttribute("translate");
      } else {
        element.setAttribute("translate", translateAttribute);
      }
    }
  }

  releaseScopedMask() {
    if (!this.scopedMaskedElements) return;

    this.stopScopedEventListeners();
    this.restoreMaskedElements(this.scopedMaskedElements);
    this.scopedMaskedElements = null;
  }

  handleScopedNavigation(event) {
    if (this.scopedNavigationReloading) return;

    if (event.type === "popstate" && this.scopedMaskedElements) {
      // Turbo replaces conversation nodes on Back, but Google retains references
      // to the old nodes. A page restore needs a fresh widget, just like entering
      // Messages through beforeVisit. Do not translate the restored inbox to en.
      const pageLanguage = localStorage.getItem("selected_lang") || "en";
      pageLanguage === "en"
        ? this.clearTranslationCookies()
        : this.setTranslationCookie(pageLanguage);
      // Keep the current translated view painted until the new page is ready;
      // do not let Turbo first replace it with an untranslated cached snapshot.
      event.stopImmediatePropagation();
      this.scopedNavigationReloading = true;
      this.stopScopedEventListeners();
      globalThis.location.replace(globalThis.location.href);
      return;
    }
    this.clearConversationTranslation();
  }

  startScopedEventListeners() {
    if (this.scopedEventListenersActive) return;

    this.scopedEventListenersActive = true;
    document.addEventListener("turbo:before-visit", this.handleNavigationStart);
    globalThis.addEventListener("popstate", this.handleNavigationStart, true);
    globalThis.addEventListener("pagehide", this.handleNavigationStart);
    document.addEventListener("show.bs.modal", this.handleModalShow);
  }

  stopScopedEventListeners() {
    if (!this.scopedEventListenersActive) return;

    this.scopedEventListenersActive = false;
    document.removeEventListener("turbo:before-visit", this.handleNavigationStart);
    globalThis.removeEventListener("popstate", this.handleNavigationStart, true);
    globalThis.removeEventListener("pagehide", this.handleNavigationStart);
    document.removeEventListener("show.bs.modal", this.handleModalShow);
  }

  clearConversationTranslation() {
    if (this.conversationResetPromise) return this.conversationResetPromise;
    this.conversationResetPromise = this.resetConversationWidget().finally(() => {
      this.conversationResetPromise = null;
    });
    return this.conversationResetPromise;
  }

  async resetConversationWidget() {
    this.cleanupScopedTranslation();
    this.resetScopedLanguage();
    if (!this.scopedMaskedElements) return;

    const pageLanguage = localStorage.getItem("selected_lang") || "en";
    const gtCombo = document.querySelector(".goog-te-combo");
    let option = gtCombo?.querySelector(`option[value="${pageLanguage}"]`);
    if (gtCombo && !option && pageLanguage === this.pageLanguageValue) {
      option = this.createLanguageOption(pageLanguage, "Original language");
      gtCombo.appendChild(option);
    }
    // Google's page reset also rewrites protected message nodes. Preserve the
    // local text so its language marker still describes what the user sees.
    const translatedMessages = Array.from(document.querySelectorAll(
      "[data-message-translate-target=\"content\"][data-local-translation-language]"
    ), message => this.captureMessageSnapshot(message));

    try {
      if (gtCombo && option && gtCombo.value !== pageLanguage) {
        pageLanguage === "en"
          ? this.clearTranslationCookies()
          : this.setTranslationCookie(pageLanguage);

        const resetDone = this.waitForTranslationDone();
        gtCombo.value = pageLanguage;
        gtCombo.dispatchEvent(new Event("change", { bubbles: true }));
        await this.nextFrame();
        await resetDone;
      }
    } finally {
      for (const snapshot of translatedMessages) {
        if (snapshot.node.isConnected) this.restoreMessageSnapshot(snapshot);
      }
      this.releaseScopedMask();
    }
  }

  enableGlobalMessageTranslation() {
    for (const element of document.querySelectorAll(
      "nav.sidebar, .main-header, #user-list, .modal, [role='dialog'], .swal2-container, [data-local-translation-language]"
    )) {
      element.classList.remove("notranslate");
      element.removeAttribute("translate");
      delete element.dataset.localTranslationLanguage;
    }
  }

  async translateTextSegments(segments, targetLanguage, cache = new Map()) {
    const translated = new Array(segments.length);
    let nextIndex = 0;

    const translateNext = async () => {
      while (nextIndex < segments.length) {
        const index = nextIndex++;
        const text = segments[index];
        const cached = cache.get(text);

        if (cached) {
          translated[index] = await cached;
          continue;
        }

        const request = this.translateSingleText(text, targetLanguage).catch(error => {
          cache.delete(text);
          throw error;
        });
        cache.set(text, request);
        translated[index] = await request;
      }
    };

    const workerCount = Math.min(6, segments.length);
    await Promise.all(
      Array.from({ length: workerCount }, () => translateNext())
    );
    return translated;
  }

  async translateInterfaceTexts(texts) {
    const language = localStorage.getItem("selected_lang") || "en";
    if (language === "en") return texts;

    const indexes = [];
    const sourceTexts = [];

    for (const [index, text] of texts.entries()) {
      if (!text?.trim()) continue;
      indexes.push(index);
      sourceTexts.push(text);
    }

    if (sourceTexts.length === 0) return texts;

    try {
      const translations = await this.translateTextSegments(sourceTexts, language);
      const translatedTexts = [...texts];

      for (const [translationIndex, originalIndex] of indexes.entries()) {
        translatedTexts[originalIndex] = translations[translationIndex];
      }

      return translatedTexts;
    } catch (error) {
      console.warn("Interface text translation failed", error);
      return texts;
    }
  }

  async translateSingleText(text, targetLanguage) {
    if (targetLanguage === "en" || !text.trim()) return text;

    const combo = document.querySelector(".goog-te-combo");
    if (!combo || combo.value !== targetLanguage) {
      throw new Error("The translation widget is not ready for this language");
    }

    // Keep the translation text in the viewport. The widget can skip labels
    // placed off-screen, even when a new translation pass is requested.
    const container = document.createElement("div");
    container.setAttribute("aria-hidden", "true");
    container.setAttribute("translate", "yes");
    Object.assign(container.style, {
      position: "fixed",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      width: "min(400px, 90vw)",
      opacity: "0",
      pointerEvents: "none"
    });
    const label = document.createElement("span");
    label.textContent = text;
    container.appendChild(label);
    const translated = this.waitForTranslationDone({
      root: label,
      requireMutation: true
    });
    document.body.appendChild(container);

    try {
      // Modal actions can insert a flash after the widget's last translation
      // pass. Request a new pass rather than relying on mutation auto-detection.
      combo.dispatchEvent(new Event("change", { bubbles: true }));
      const ready = await translated;
      if (!ready || combo.value !== targetLanguage) {
        throw new Error("The translation widget could not translate this label");
      }
      return label.textContent;
    } finally {
      container.remove();
    }
  }

  showScopedTranslationError(error) {
    const message = error?.message || "Unable to translate the selected messages";

    if (globalThis.Swal) {
      globalThis.Swal.fire({
        icon: "error",
        title: "Translation unavailable",
        text: message
      });
    } else {
      console.error(message);
    }
  }

  cleanupScopedTranslation() {
    const scope = this.scopedTranslation;
    if (!scope) {
      this.setGlobalTranslatorVisible(true);
      return;
    }

    if (globalThis.jQuery && this.hasScopedSelectTarget) {
      jQuery(this.scopedSelectTarget).off("select2:close.itoonsScopedTranslation");
    }

    scope.target.removeAttribute("aria-busy");
    this.scopedTranslation = null;
    this.scopedSelectTarget?.closest(".goog-te-gadget")?.classList.add("d-none");
    this.setGlobalTranslatorVisible(true);
  }

  resetScopedLanguage() {
    if (!this.hasScopedSelectTarget) return;

    this.scopedSelectTarget.value = "en";
    this.scopedSelectTarget.dataset.switching = "0";
    this.scopedSelectTarget.disabled = false;

    if (globalThis.jQuery) {
      jQuery(this.scopedSelectTarget).trigger("change.select2");
    }
  }

  setGlobalTranslatorVisible(visible) {
    const globalSelectWrapper = this.hasCustomSelectTarget
      ? this.customSelectTarget.closest(".goog-te-gadget")
      : null;

    if (!visible) {
      globalSelectWrapper?.classList.add("d-none");
      return;
    }

    const translatorReady = this.hasCustomSelectTarget &&
      this.customSelectTarget.options.length > 1;

    globalSelectWrapper?.classList.remove("d-none");
    this.setLanguageDropdownLoading(!translatorReady);
  }

  setLanguageDropdownLoading(loading) {
    if (!this.hasGlobalSelectWrapperTarget) return;

    this.globalSelectWrapperTarget.classList.toggle(
      "google-translate-box--loading",
      loading
    );
    this.globalSelectWrapperTarget.setAttribute("aria-busy", loading.toString());

    if (this.hasCustomSelectTarget) {
      this.customSelectTarget.setAttribute(
        "aria-label",
        loading ? "Translation languages are loading" : "Choose translation language"
      );
    }
  }

  setSwitchingState(switching) {
    if (!this.hasCustomSelectTarget) return;

    this.customSelectTarget.dataset.switching = switching ? "1" : "0";
    this.customSelectTarget.disabled = switching;

    if (this.hasStatusTarget) {
      this.statusTarget.classList.toggle("d-none", !switching);
    }

    if (switching && this.hasStatusTextTarget) {
      const language = this.customSelectTarget.selectedOptions[0]?.textContent;
      this.statusTextTarget.textContent = language
        ? `Changing language to ${language}…`
        : "Changing language…";
    }

    if (globalThis.jQuery) {
      jQuery(this.customSelectTarget).trigger("change.select2");
    }
  }

  setupGoogleTranslate() {
    globalThis.__gtSuppressLangChange = false;

    globalThis.googleTranslateElementInit = () => {
      setTimeout(() => {
        const isInitialized = document.querySelector(".goog-te-combo");
        const translateContainer = document.getElementById("google_translate_element");

        if (!isInitialized && translateContainer && globalThis.google?.translate) {
          this.translateElement = new google.translate.TranslateElement(
            {
              pageLanguage: this.pageLanguageValue,
              autoDisplay: false
            },
            "google_translate_element"
          );
        }
      }, 1000);
    };

    if (globalThis.google?.translate) {
      globalThis.googleTranslateElementInit();
    } else {
      this.loadGoogleScript();
    }
  }

  loadGoogleScript() {
    const existingScript = document.querySelector("script[data-google-translate=\"true\"]");
    if (existingScript) {
      if (globalThis.google?.translate) {
        globalThis.googleTranslateElementInit?.();
        return;
      }

      if (!this.googleScriptRetryTimer) {
        this.googleScriptRetryTimer = setTimeout(() => {
          this.googleScriptRetryTimer = null;
          if (globalThis.google?.translate) {
            globalThis.googleTranslateElementInit?.();
            return;
          }

          existingScript.remove();
          this.loadGoogleScript();
        }, 3000);
      }
      return;
    }

    const script = document.createElement("script");

    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

    script.async = true;
    script.dataset.googleTranslate = "true";

    script.onload = () => {
      console.log("[GT] script loaded");
      globalThis.googleTranslateElementInit?.();
      this.startPollingGoogleCombo();
    };

    script.onerror = () => {
      console.error("[GT] script failed, retrying...");
      script.remove();
      clearTimeout(this.googleScriptRetryTimer);
      this.googleScriptRetryTimer = null;

      setTimeout(() => {
        this.loadGoogleScript();
      }, 3000);
    };

    document.head.appendChild(script);

    setTimeout(() => {
      if (script.isConnected && !globalThis.google?.translate) {
        console.warn("[GT] script stuck, retrying...");
        script.remove();
        clearTimeout(this.googleScriptRetryTimer);
        this.googleScriptRetryTimer = null;
        this.loadGoogleScript();
      }
    }, 10000);
  }

  startPollingGoogleCombo() {
    if (!this.hasCustomSelectTarget) return;

    this.clearPollTimer();

    // Script callbacks may restart polling; keep the original loading deadline.
    if (!this.languageLoadTimeout) {
      this.languageLoadTimeout = setTimeout(() => {
        this.setLanguageDropdownLoading(false);
        if (this.hasLanguageLoadStatusTarget) {
          this.languageLoadMessageTarget.textContent =
            "Languages are taking longer than expected to load. Reload the page, then try choosing your language again.";
          this.languageLoadReloadTarget.classList.remove("d-none");
        }
        for (const select of this.getCustomSelects()) {
          if (select.options.length > 1) continue;
          const option = select.options[0];
          if (option) option.textContent = "Languages unavailable. Please reload the page.";
          this.refreshSelect(select, true);
        }
      }, this.pollTimeoutValue);
    }

    this.pollTimer = setInterval(() => {
      const gtCombo = document.querySelector(".goog-te-combo");
      if (!gtCombo || gtCombo.options.length < 2) return;

      this.syncLanguageDropdown(gtCombo);
      this.clearPollTimer();

      if (this.shouldOpenSelect2AfterLoad) {
        this.shouldOpenSelect2AfterLoad = false;

        setTimeout(() => {
          this.openSelect2();
        }, 250);
      }
    }, this.pollIntervalValue);
  }

  syncLanguageDropdown(gtCombo) {
    if (!this.hasCustomSelectTarget) return;

    this.clearLanguageLoadStatus();
    this.populateCustomDropdown(gtCombo);
    gtCombo.style.display = "none";
    this.restoreSelectedLanguage();
    this.setLanguageDropdownLoading(false);
    this.showSelect2Container();
    this.finishInitialTranslation(gtCombo);
  }

  async finishInitialTranslation(gtCombo) {
    const guard = globalThis.itoonsTranslationGuard;
    if (!guard?.active) return;

    const language = guard.language;
    const option = gtCombo.querySelector(`option[value="${language}"]`);

    if (language === "en" || !option) {
      this.revealTranslatedPage();
      return;
    }

    const translationAlreadyApplied =
      gtCombo.value === language &&
      (document.body.classList.contains("translated-ltr") ||
        document.body.classList.contains("translated-rtl") ||
        document.documentElement.classList.contains("translated-ltr") ||
        document.documentElement.classList.contains("translated-rtl"));

    if (translationAlreadyApplied) {
      this.revealTranslatedPage();
      return;
    }

    try {
      this.setTranslationCookie(language);
      const translationDone = this.waitForTranslationDone({ requireMutation: true });
      gtCombo.value = language;
      gtCombo.dispatchEvent(new Event("change", { bubbles: true }));
      await this.nextFrame();
      await translationDone;
    } finally {
      this.revealTranslatedPage();
    }
  }

  revealTranslatedPage() {
    globalThis.itoonsTranslationGuard?.reveal();
  }

  populateCustomDropdown(gtCombo) {
    const selects = this.getCustomSelects();
    const options = this.buildLanguageOptions(gtCombo);

    for (const select of selects) {
      this.populateLanguageSelect(select, options);
    }
  }

  getCustomSelects() {
    return this.hasScopedSelectTarget
      ? [this.customSelectTarget, this.scopedSelectTarget]
      : [this.customSelectTarget];
  }

  buildLanguageOptions(gtCombo) {
    const options = [this.createLanguageOption("en", "English")];

    for (const option of gtCombo.options) {
      if (!option.value || option.value === "en") continue;

      options.push(
        this.createLanguageOption(option.value, option.textContent)
      );
    }

    return options.sort((a, b) =>
      a.textContent.localeCompare(b.textContent)
    );
  }

  createLanguageOption(value, text) {
    const option = document.createElement("option");

    option.value = value;
    option.textContent = text;

    return option;
  }

  populateLanguageSelect(select, options) {
    const wasLoading = select.dataset.languagesLoading === "1";
    const currentValue = select === this.customSelectTarget
      ? localStorage.getItem("selected_lang") || "en"
      : select.value;

    select.innerHTML = "";

    this.appendLanguageOptions(select, options);
    this.setSelectedLanguage(select, currentValue);
    this.resetLanguageSelect(select);

    this.refreshSelect(select, wasLoading);
  }

  appendLanguageOptions(select, options) {
    for (const option of options) {
      select.appendChild(option.cloneNode(true));
    }
  }

  setSelectedLanguage(select, currentValue) {
    const hasCurrentValue =
      currentValue &&
      select.querySelector(`option[value="${currentValue}"]`);

    select.value = hasCurrentValue ? currentValue : "en";
  }

  resetLanguageSelect(select) {
    select.disabled = false;
    delete select.dataset.languagesLoading;
    select.setAttribute("aria-label", "Choose translation language");
  }

  refreshSelect(select, wasLoading) {
    if (!globalThis.jQuery) return;

    const $select = jQuery(select);
    const select2 = $select.data("select2");

    $select.trigger("change.select2");

    if (wasLoading && select2?.isOpen()) {
      this.refreshSelect2Search(select2);
    }
  }

  refreshSelect2Search(select2) {
    const search = document.querySelector(
      ".select2-container--open .select2-search__field"
    );

    select2.trigger("query", {
      term: search?.value || ""
    });
  }

  refreshSelect2() {
    // if (!globalThis.jQuery || !this.hasCustomSelectTarget) return:

    const $select = jQuery(this.customSelectTarget);

    // if ($select.data("select2")) {:
    $select.trigger("change.select2");
    // }
  }

  select2Open() {
    setTimeout(() => {
      const input = document.querySelector(".select2-container--open .select2-search__field");
      if (input) input.focus();
    }, 50);
  }

  restoreSelectedLanguage(displayedLanguage = null) {
    const last = displayedLanguage || localStorage.getItem("selected_lang") || "en";

    if (!this.customSelectTarget.querySelector(`option[value="${last}"]`)) return;

    globalThis.__gtSuppressLangChange = true;
    this.customSelectTarget.value = last;
    this.refreshSelect2();

    setTimeout(() => {
      globalThis.__gtSuppressLangChange = false;
    }, 0);
  }

  setTranslationCookie(language) {
    const value = `/en/${language}`;
    const host = globalThis.location.hostname;
    const configuredDomain = this.cookieDomainValue
      ?.trim()
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      .replace(/:\d+$/, "")
      .replace(/^\.+/, "");

    document.cookie = `googtrans=${value}; path=/; SameSite=Lax`;

    const rootDomain = configuredDomain || host.split(".").slice(-2).join(".");
    if (rootDomain !== host) {
      document.cookie =
        `googtrans=${value}; path=/; domain=.${rootDomain}; SameSite=Lax`;
    }
  }

  initializeBrowserLanguageSession() {
    const browserSessionActive = document.cookie
      .split("; ")
      .includes("itoons_language_session=1");

    if (browserSessionActive) return;

    this.clearTranslationCookies();
    localStorage.setItem("selected_lang", "en");
    document.cookie = "itoons_language_session=1; path=/; SameSite=Lax";
  }

  async resetTranslation() {
    this.clearTranslationCookies();
    globalThis.location.reload();
  }

  clearTranslationCookies() {
    const host = globalThis.location.hostname;
    const configuredDomain = this.cookieDomainValue
      ?.trim()
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      .replace(/:\d+$/, "")
      .replace(/^\.+/, "");
    this.deleteCookie("googtrans");
    this.deleteCookie("googtrans", "." + host);

    const rootDomain = configuredDomain || host.split(".").slice(-2).join(".");
    if (rootDomain !== host) {
      this.deleteCookie("googtrans", "." + rootDomain);
    }
  }

  waitForTranslationDone(
    {
      timeoutMs = this.pollTimeoutValue,
      stableMs = this.stableMsValue,
      requireMutation = false,
      root = document.body
    } = {}
  ) {
    return new Promise((resolve) => {
      let lastChange = Date.now();
      let mutationSeen = false;
      const start = Date.now();

      const observer = new MutationObserver(() => {
        mutationSeen = true;
        lastChange = Date.now();
      });

      observer.observe(root, {
        subtree: true,
        childList: true,
        characterData: true
      });

      const tick = setInterval(() => {
        const now = Date.now();

        if ((!requireMutation || mutationSeen) && now - lastChange > stableMs) {
          cleanup();
          resolve(true);
          return;
        }

        if (now - start > timeoutMs) {
          cleanup();
          resolve(false);
        }
      }, 100);

      const cleanup = () => {
        clearInterval(tick);
        observer.disconnect();
      };
    });
  }

  deleteCookie(name, domain = null) {
    document.cookie =
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;` +
      (domain ? ` domain=${domain};` : "");
  }

  nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  clearPollTimer() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
